import React from 'react';
import { Card, Descriptions, Typography, Divider, Alert, Table } from 'antd';
import {AppapproDTO} from "@/shared/api/model";
import { formatCurrency, parseJavaDateArray } from '@/shared/utils/formatters';

const { Title, Text } = Typography;

interface LoanGuarantorViewProps {
    data?: {
        valueData: AppapproDTO;
    };
    formikProps?: any;
}

/**
 * LoanGuarantorView displays relevant information for a guarantor
 * to make an informed decision about a loan application
 */
const LoanGuarantorView: React.FC<LoanGuarantorViewProps> = ({ data, formikProps }) => {
    const values = formikProps?.values || data || {};
    const valueData = values.valueData as AppapproDTO;
    
    // The backend already filters for loans where this user is a guarantor
    // so we don't need to filter again, just display the guarantor information
    const currentUserId = localStorage.getItem('userId') || '';
    
    // Calculate loan details
    const loanAmount = valueData?.loanAmount || 0;
    const loanPeriod = valueData?.loanPeriod || 0;
    const interestRate = valueData?.intRate || 0;
    const monthlyInstallment = valueData?.newInstallment || 0;
    
    // Calculate guarantor's liability
    const guarantorsCount = valueData?.guarantors?.length || 1;
    const guarantorLiability = loanAmount / guarantorsCount;
    
    // Applicant information
    const applicantName = valueData?.name || '';
    const applicantId = valueData?.idNo || '';
    const applicantPhone = valueData?.phoneNo || '';
    
    return (
        <Card className="w-full">
            <Title level={4}>Loan Guarantor Information</Title>
            
            <Alert
                message="You are a guarantor for this loan application"
                description="Please review the details below to make an informed decision."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />
            
            <Divider orientation="left">Applicant Information</Divider>
            <Descriptions bordered column={2}>
                <Descriptions.Item label="Applicant Name">{applicantName}</Descriptions.Item>
                <Descriptions.Item label="ID Number">{applicantId}</Descriptions.Item>
                <Descriptions.Item label="Phone Number">{applicantPhone}</Descriptions.Item>
                <Descriptions.Item label="Application Date">
                    {valueData?.appliDate ? parseJavaDateArray(valueData.appliDate)?.toLocaleDateString() || 'N/A' : 'N/A'}
                </Descriptions.Item>
            </Descriptions>
            
            <Divider orientation="left">Loan Details</Divider>
            <Descriptions bordered column={2}>
                <Descriptions.Item label="Loan Product">{valueData?.productName || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Loan Amount">{formatCurrency(loanAmount)}</Descriptions.Item>
                <Descriptions.Item label="Loan Period">{loanPeriod} months</Descriptions.Item>
                <Descriptions.Item label="Interest Rate">{interestRate}%</Descriptions.Item>
                <Descriptions.Item label="Monthly Installment">{formatCurrency(monthlyInstallment)}</Descriptions.Item>
                <Descriptions.Item label="Maturity Date">
                    {valueData?.maturityDate ? parseJavaDateArray(valueData.maturityDate)?.toLocaleDateString() || 'N/A' : 'N/A'}
                </Descriptions.Item>
            </Descriptions>
            
            <Divider orientation="left">Your Guarantor Responsibility</Divider>
            <Alert
                message="Your Financial Liability"
                description={
                    <div>
                        <p>As a guarantor, you are responsible for helping the borrower repay this loan if they default. Your share of liability is:</p>
                        <Title level={3}>{formatCurrency(guarantorLiability)}</Title>
                        <p>This is calculated as an equal share among all guarantors.</p>
                    </div>
                }
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
            />
            
            {valueData?.guarantors && valueData.guarantors.length > 0 && (
                <>
                    <Divider orientation="left">All Guarantors</Divider>
                    <Table
                        dataSource={valueData.guarantors.map((g, idx) => ({ ...g, key: idx }))}
                        columns={[
                            {
                                title: 'Name',
                                dataIndex: 'name',
                                key: 'name',
                            },
                            {
                                title: 'ID Number',
                                dataIndex: 'idNo',
                                key: 'idNo',
                            },
                            {
                                title: 'Phone',
                                dataIndex: 'phoneNo',
                                key: 'phoneNo',
                            },
                            {
                                title: 'Status',
                                dataIndex: 'status',
                                key: 'status',
                                render: (status) => (
                                    <span style={{ color: status === 'APPROVED' ? 'green' : status === 'REJECTED' ? 'red' : 'orange' }}>
                                        {status || 'PENDING'}
                                    </span>
                                ),
                            },
                        ]}
                        pagination={false}
                        size="small"
                    />
                </>
            )}
        </Card>
    );
};

export default LoanGuarantorView;
